import {makeAutoObservable, observable, reaction, runInAction} from 'mobx';

import type {ApiCountResponse} from '../../../../api/types';
import type {
  CoreCoachInvitesListParams,
  CoreCoachMembershipsListParams,
} from '../../../../api/types/core/methods';
import type {CoreMembershipApiModel} from '../../../../api/types/core/models';
import type {ApiRpcQuery} from '../../../../api/utils/apiRequest';
import type {CollectionStore} from '../../../../api/utils/collectionStore';
import type {
  BatchMethodStatus,
  MethodStatus,
} from '../../../../api/utils/entityStore';
import type {
  Destructible,
  WithBusyManager,
  WithReactionDispose,
} from '../../../../utils/store';
import type {ProgramInvite, ProposedMembership, RegularInvite} from './types';

import {inviteStore} from '../../../../api/invite';
import {membershipStore} from '../../../../api/membership';
import {apiRequest} from '../../../../api/utils/apiRequest';
import {createCollectionStore} from '../../../../api/utils/collectionStore';
import {alert} from '../../../../components/alert';
import {inviteDelete} from '../../../../services/entity-action/invite/invite.delete';
import {inviteResend} from '../../../../services/entity-action/invite/invite.resend';
import {isCancelCommand} from '../../../../services/entity-action/utils';
import {dateTimeObjToInt} from '../../../../utils/datetime';
import {getEntityTypeById} from '../../../../utils/entity/getEntityType';
import {
  emitter,
  WS_RECEIVE_MESSAGE_EVENT,
} from '../../../../utils/eventEmitter';
import {logger} from '../../../../utils/logger';
import {
  AsyncBusyManager,
  ReactionDisposeManager,
} from '../../../../utils/store';
import {proposedMembershipExpand, regularInviteExpand} from './utils';

type Config = {
  onCountChange?: (count: number) => void;
};

type ReasonKey = 'cancel' | 'fetch' | 'fetchMore' | 'resend';

const LIMIT = 20;

const PROPOSED_MEMBERSHIPS_LIMIT = 300;

const CURSOR_DIRECTION = 1;

export class ProgramInvitesStore
  implements Destructible, WithBusyManager<ReasonKey>, WithReactionDispose
{
  readonly busy = new AsyncBusyManager<ReasonKey>();
  count = 0;
  disposer = new ReactionDisposeManager();
  invites: ProgramInvite[] = [];
  isFetching = true;
  isLoaded = false;
  private invitesStore: CollectionStore<
    RegularInvite,
    CoreCoachInvitesListParams
  >;
  private readonly invitesStoreParams: CoreCoachInvitesListParams;
  private isPaginating = false;
  private membershipsStore: CollectionStore<
    ProposedMembership,
    CoreCoachMembershipsListParams
  >;
  private readonly membershipsStoreParams: CoreCoachMembershipsListParams;

  constructor(
    public programId: string,
    private config?: Config,
  ) {
    this.invitesStoreParams = {
      cursor: [CURSOR_DIRECTION, 0, null],
      expand: regularInviteExpand,
      limit: LIMIT,
      query: [['program_id', '==', this.programId]],
    };

    this.membershipsStoreParams = {
      expand: proposedMembershipExpand,
      limit: LIMIT,
      query: [
        ['program_id', '==', programId],
        ['status', 'in', ['proposed']] as ApiRpcQuery<
          CoreMembershipApiModel,
          'status'
        >,
      ],
      sort: [['created', CURSOR_DIRECTION]],
    };

    this.invitesStore = createCollectionStore({
      method: 'coach.invites.list',
      params: this.invitesStoreParams,
      withCount: true,
    });

    this.membershipsStore = createCollectionStore({
      method: 'coach.memberships.list',
      params: this.membershipsStoreParams,
      withCount: true,
    });

    this.busy.observe(this, {
      cancel: 'cancel',
      fetch: 'fetch',
      fetchMore: 'fetchMore',
      resend: 'resend',
    });

    this.setupReactions();

    makeAutoObservable(
      this,
      {
        invites: observable.shallow,
      },
      {autoBind: true},
    );

    emitter.on(WS_RECEIVE_MESSAGE_EVENT, this.handleWsMessage);

    this.updateCount();
  }

  destruct() {
    emitter.off(WS_RECEIVE_MESSAGE_EVENT, this.handleWsMessage);

    this.invitesStore.clear();
  }

  get countString() {
    return this.count > 0 ? this.count.toString() : '';
  }

  get hasMore() {
    /**
     * as we merge two collections, we paginate only by regular invites
     * because it's supposed to be that invites by email (regular invites)
     * will be more than invites by user id (proposed membership)
     */
    return Boolean(this.invitesStore.cursor.next);
  }

  get isEmpty() {
    return this.invites.length === 0 && this.isLoaded;
  }

  get isInviteActionInProgress() {
    return this.busy.getReason('cancel') || this.busy.getReason('resend');
  }

  async cancel(invite: ProgramInvite) {
    try {
      const inviteEntityType = getEntityTypeById(invite._id);

      const result = await inviteDelete({invite});

      if (isCancelCommand(result)) {
        return;
      }

      if (inviteEntityType === 'invite') {
        this.invitesStore.removeItem(invite as RegularInvite);
      } else {
        this.membershipsStore.removeItem(invite as ProposedMembership);
      }

      this.removeInvite(invite);
    } catch (error) {
      logger.error(error);

      alert.error(error);
    }
  }

  async fetch(silent = false) {
    try {
      runInAction(() => {
        this.isFetching = !silent;
      });

      const regularInvitesFetch = this.invitesStore.fetch(
        {
          limit: Math.max(this.invitesStore.items.length, LIMIT),
        },
        {silent},
      );

      const proposedMembershipsFetch = this.membershipsStore.fetch(
        {
          limit: PROPOSED_MEMBERSHIPS_LIMIT,
        },
        {silent},
      );

      const [regularInvites, proposedMemberships] = await Promise.all([
        regularInvitesFetch,
        proposedMembershipsFetch,
      ]);

      runInAction(() => {
        this.invites = [...regularInvites, ...proposedMemberships].sort(
          (a, b) => dateTimeObjToInt(b.created) - dateTimeObjToInt(a.created),
        );

        this.updateCount();
      });
    } catch (error) {
      logger.error(error);
    } finally {
      runInAction(() => {
        this.isLoaded = true;
        this.isFetching = false;
      });
    }
  }

  async fetchMore() {
    if (this.isPaginating) {
      return;
    }

    this.isPaginating = true;

    if (this.invitesStore.cursor.next) {
      try {
        const result = await this.invitesStore.fetch(
          {
            cursor: this.invitesStore.cursor.next,
          },
          {
            appendTo: 'end',
            silent: true,
          },
        );

        runInAction(() => {
          this.invites.push(...result);
        });
      } catch (error) {
        logger.error(error);
      }
    }

    this.isPaginating = false;
  }

  async resend(invite: ProgramInvite) {
    try {
      await inviteResend.asCoach({
        invite,
      });

      this.fetch(true);
    } catch (error) {
      logger.error(error);

      alert.error(error);
    }
  }

  private handleWsMessage(message) {
    if (
      message.event.type === 'membership_created' &&
      message.program._id === this.programId
    ) {
      this.fetch(true);
    }
  }

  private removeInvite(invite: ProgramInvite) {
    this.invites = this.invites.filter(item => item._id !== invite._id);
    this.updateCount();
  }

  private setupReactions() {
    this.disposer.watchMany<BatchMethodStatus<unknown> | MethodStatus<unknown>>(
      {
        condition: status => status.success,
        effect: () => {
          this.fetch(true);
        },
        reactionConfigs: [
          {expression: () => inviteStore.sending, key: 'inviteSending'},
          {
            expression: () => inviteStore.sendingToProgram,
            key: 'inviteSendingToProgram',
          },
          {
            expression: () => membershipStore.coach.creating,
            key: 'membershipCreating',
          },
          {
            expression: () => membershipStore.coach.creatingBatch,
            key: 'membershipCreatingBatch',
          },
        ],
      },
    );

    this.disposer.watch(
      'deleting',
      reaction(
        () => inviteStore.deleting,
        deleting => {
          if (deleting.success && deleting.entity) {
            const invite = deleting.entity as RegularInvite;

            this.invitesStore.removeItem(invite);

            this.removeInvite(invite);

            this.fetch(true);
          }
        },
      ),
    );
  }

  private async updateCount() {
    if (this.invitesStore.isLoaded && this.membershipsStore.isLoaded) {
      this.count = this.invitesStore.count + this.membershipsStore.count;
    } else {
      try {
        const batchResult: ApiCountResponse[] = await apiRequest({
          batch: [
            {
              method: 'coach.invites.count',
              params: {
                query: this.invitesStoreParams.query,
              },
            },
            {
              method: 'coach.memberships.count',
              params: {
                query: this.membershipsStoreParams.query,
              },
            },
          ],
        });

        const [invitesResponse, membershipsResponse] = batchResult;

        runInAction(() => {
          this.count = invitesResponse!._count + membershipsResponse!._count;
        });
      } catch (e) {
        logger.error(e);
      }
    }

    this.config?.onCountChange?.(this.count);
  }
}
